import {
  type DtoCreateHomeReq,
  type DtoHome,
  type DtoUpdateHomeReq,
  type DomainDownPaymentSourceType,
  type DomainHomeSectionType,
  type DomainHomeUsageType,
  type DomainManufacturerType,
} from "@porchpass/api-client";
import {
  DLA_DOWN_PAYMENT_SOURCE_GIFT,
  DLA_DOWN_PAYMENT_SOURCE_OTHER,
  DLA_HOME_MANUFACTURER_OTHER,
  DLA_HOME_SECTION_OTHER,
  DLA_HOME_USAGE_OTHER,
} from "src/forms/validation/conditionalOtherConfigs";
import { otherTextForApi } from "src/forms/validation/conditionalOther";
import { safeNumber } from "src/utils";

export type HomeUsageFormData = {
  usage_type?: DomainHomeUsageType;
  usage_type_other?: string;
  user_first_visit?: boolean;
};

export type HomePlacementFormData = {
  street1?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  county?: string;
};

export type HomeManufactureDetailsFormData = {
  manufacturer_type?: DomainManufacturerType;
  manufacturer_type_other?: string;
  model_name?: string;
  model_year?: string;
  section_type?: DomainHomeSectionType;
  section_type_other?: string;
};

export type HomeDownPaymentFormData = {
  price?: string;
  down_payment_amount?: string;
  down_payment_giftor_name?: string;
  down_payment_source_other?: string;
  down_payment_source_type?: DomainDownPaymentSourceType;
  site_improvement_cost?: string;
  upgrades_cost?: string;
};

/** API defaults unset numeric fields to 0; treat those as empty in the form. */
function toUnsetAmountString(
  value?: number | string | null,
): string | undefined {
  if (value == null || value === "") return undefined;
  const num = Number(value);
  if (Number.isNaN(num) || num === 0) return undefined;
  return String(value);
}

export function fromHomeUsage(data: DtoHome): HomeUsageFormData {
  return {
    usage_type: data?.usage_type,
    usage_type_other: data?.usage_type_other,
    user_first_visit: data?.price === undefined,
  };
}

export function toCreateHomeUsageReq(
  data: HomeUsageFormData,
): DtoCreateHomeReq {
  return {
    usage_type: data?.usage_type,
    usage_type_other: otherTextForApi(
      data?.usage_type,
      data?.usage_type_other,
      DLA_HOME_USAGE_OTHER,
    ),
  };
}

export function toUpdateHomeUsageReq(
  data: HomeUsageFormData,
): DtoUpdateHomeReq {
  return {
    usage_type: data?.usage_type,
    usage_type_other: otherTextForApi(
      data?.usage_type,
      data?.usage_type_other,
      DLA_HOME_USAGE_OTHER,
    ),
  };
}

export function fromHomePlacement(data: DtoHome): HomePlacementFormData {
  return {
    ...data?.address,
  };
}

export function toUpdateHomePlacementReq(
  data: HomePlacementFormData,
): DtoUpdateHomeReq {
  return {
    ...(Object.values(data).filter((v) => !!v).length
      ? {
          address: {
            street1: data.street1 ?? undefined,
            city: data.city ?? undefined,
            state: data.state ?? undefined,
            zip_code: data.zip_code ?? undefined,
            county: data.county ?? undefined,
          },
        }
      : {}),
  };
}

export function fromHomeManufactureDetails(
  data: DtoHome,
): HomeManufactureDetailsFormData {
  return {
    manufacturer_type: data?.manufacturer_type,
    manufacturer_type_other: data?.manufacturer_type_other,
    model_name: data?.model_name,
    model_year: data?.model_year === 0 ? "" : data?.model_year?.toString(),
    section_type: data?.section_type,
    section_type_other: data?.section_type_other,
  };
}

export function toUpdateHomeManufactureDetailsReq(
  data: HomeManufactureDetailsFormData,
): DtoUpdateHomeReq {
  return {
    manufacturer_type: data?.manufacturer_type,
    manufacturer_type_other: otherTextForApi(
      data?.manufacturer_type,
      data?.manufacturer_type_other,
      DLA_HOME_MANUFACTURER_OTHER,
    ),
    model_name: data?.model_name,
    model_year: safeNumber(data?.model_year),
    section_type: data?.section_type,
    section_type_other: otherTextForApi(
      data?.section_type,
      data?.section_type_other,
      DLA_HOME_SECTION_OTHER,
    ),
  };
}

export function fromHomeDownPayment(data: DtoHome): HomeDownPaymentFormData {
  return {
    price: toUnsetAmountString(data?.price),
    down_payment_amount: data?.down_payment_amount?.toString(),
    down_payment_giftor_name: data?.down_payment_giftor_name,
    down_payment_source_other: data?.down_payment_source_other,
    down_payment_source_type: data?.down_payment_source_type,
    site_improvement_cost: toUnsetAmountString(data?.site_improvement_cost),
    upgrades_cost: toUnsetAmountString(data?.upgrades_cost),
  };
}

export function toUpdateHomeDownPaymentReq(
  data: HomeDownPaymentFormData,
): DtoUpdateHomeReq {
  const downPaymentAmount = safeNumber(data?.down_payment_amount);

  return {
    price: safeNumber(data?.price),
    down_payment_amount: downPaymentAmount,
    ...(downPaymentAmount > 0
      ? {
          down_payment_source_type: data?.down_payment_source_type,
          down_payment_source_other: otherTextForApi(
            data?.down_payment_source_type,
            data?.down_payment_source_other,
            DLA_DOWN_PAYMENT_SOURCE_OTHER,
          ),
          down_payment_giftor_name: otherTextForApi(
            data?.down_payment_source_type,
            data?.down_payment_giftor_name,
            DLA_DOWN_PAYMENT_SOURCE_GIFT,
          ),
        }
      : {}),
    site_improvement_cost: safeNumber(data?.site_improvement_cost),
    upgrades_cost: safeNumber(data?.upgrades_cost),
  };
}
